import React, { useEffect, useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface ActivityRow {
  user: { id: string; name: string; email: string };
  reportsCreated: number;
  documentsUploaded: number;
  chatsStarted: number;
  lastActive: string | null;
}

interface Props {
  isOpen: boolean;
  projectId: string | null;
  projectTitle?: string;
  onClose: () => void;
}

const ProjectActivityModal: React.FC<Props> = ({ isOpen, projectId, projectTitle, onClose }) => {
  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !projectId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    const token = localStorage.getItem('riskninja-token');
    fetch(`${API_BASE_URL}/api/projects/${projectId}/activity-summary`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error('Failed to load activity');
        return r.json();
      })
      .then((data) => { if (!cancelled) setRows(data.summary || []); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, projectId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-black/50 backdrop-blur-sm" onClick={onClose} />
        <div className="inline-block w-full max-w-3xl text-left align-middle transition-all transform bg-white dark:bg-gray-800 rounded-lg shadow-xl my-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Project Activity</h3>
              {projectTitle && <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{projectTitle}</p>}
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          <div className="px-6 py-4 max-h-[65vh] overflow-y-auto">
            {loading && <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">Loading…</p>}
            {error && <p className="text-sm text-red-500 text-center py-4">{error}</p>}
            {!loading && !error && rows.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">No activity recorded for this project yet.</p>
            )}
            {!loading && rows.length > 0 && (
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    <th className="py-2 pr-4 font-medium">User</th>
                    <th className="py-2 pr-4 font-medium text-right">Reports</th>
                    <th className="py-2 pr-4 font-medium text-right">Documents</th>
                    <th className="py-2 pr-4 font-medium text-right">Chats</th>
                    <th className="py-2 font-medium">Last active</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.user.id} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                      <td className="py-2 pr-4">
                        <div className="font-medium text-gray-900 dark:text-white">{r.user.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{r.user.email}</div>
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums text-gray-900 dark:text-white">{r.reportsCreated}</td>
                      <td className="py-2 pr-4 text-right tabular-nums text-gray-900 dark:text-white">{r.documentsUploaded}</td>
                      <td className="py-2 pr-4 text-right tabular-nums text-gray-900 dark:text-white">{r.chatsStarted}</td>
                      <td className="py-2 text-gray-600 dark:text-gray-400 text-xs">
                        {r.lastActive ? new Date(r.lastActive).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectActivityModal;
